import { useContext, useEffect, useMemo, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { useDebounceState } from 'app/hooks/useDebounceState'
import { HomeStateContext } from 'features/home/homeStateContext'
import { selectedHomeSelector } from 'features/home/selectors'
import { fetchHomeThunk } from 'features/home/slice'
import {
  CloudSoftwareHistoryEnum,
  Home,
  HomeCloudSoftwareHistoryType,
  HomeSoftwareType,
  SoftwareHistoryRequestedByEnum,
} from 'features/home/types'
import { softwareFilter, requiresSoftwareFilter } from './const'
import {
  selectedHomeConfigAccessEnabledSelector,
  selectSoftwareUpdatesInProgress,
} from './HomeCloudSoftwareHistory/selectors'
import {
  fetchCloudSoftwareHistoryThunk,
  getHomeSoftwareUpdateStatusThunk,
} from './HomeCloudSoftwareHistory/slice'
import { fetchHomeSoftwareHistoryThunk } from './slice'

const ONE_SECOND = 1000
const SIX_SECONDS = ONE_SECOND * 6

export const usePullForHostUpdates = () => {
  const softwareUpdatesInProgress = useAppSelector(
    selectSoftwareUpdatesInProgress
  )
  const selectedHome = useAppSelector(selectedHomeSelector.data)

  const dispatch = useAppDispatch()
  const [poll, setPoll] = useState(false)

  const updatesInProgress =
    softwareUpdatesInProgress && softwareUpdatesInProgress.length > 0

  const [updatesWereInProgress, setUpdatesWereInProgress] = useState(
    softwareUpdatesInProgress && softwareUpdatesInProgress.length > 0
  )

  useDebounceState(poll, setPoll, SIX_SECONDS)

  function pollForEachUpdate() {
    if (!selectedHome || !selectedHome.id) {
      return () => null
    }

    if (updatesInProgress && !poll) {
      const getUpdateStatus = (
        softwareUpdate: HomeCloudSoftwareHistoryType
      ) => {
        dispatch(
          getHomeSoftwareUpdateStatusThunk({
            homeId: selectedHome.id,
            updateId: softwareUpdate.updateId,
          })
        )
      }
      softwareUpdatesInProgress.forEach(getUpdateStatus)
      setPoll(true)
    } else if (updatesWereInProgress && !updatesInProgress) {
      dispatch(
        fetchCloudSoftwareHistoryThunk({
          homeId: selectedHome.id,
          partNumber: selectedHome.hostType,
        })
      )
      dispatch(fetchHomeSoftwareHistoryThunk(selectedHome.id))
      const getHomes = () => {
        dispatch(
          fetchCloudSoftwareHistoryThunk({
            homeId: selectedHome.id,
            partNumber: selectedHome.hostType,
          })
        )
        dispatch(fetchHomeSoftwareHistoryThunk(selectedHome.id))
      }
      const getHomesAfterTimeout = setTimeout(getHomes, ONE_SECOND * 2)
      const cleanup = () => {
        if (getHomesAfterTimeout) {
          clearTimeout(getHomesAfterTimeout)
          dispatch(fetchHomeThunk(selectedHome.id))
        }
      }
      return cleanup
    }
    return () => null
  }

  useEffect(pollForEachUpdate, [
    dispatch,
    poll,
    selectedHome,
    softwareUpdatesInProgress,
    updatesInProgress,
    updatesWereInProgress,
  ])

  useEffect(() => {
    setUpdatesWereInProgress(
      softwareUpdatesInProgress && softwareUpdatesInProgress.length > 0
    )
  }, [softwareUpdatesInProgress])

  return null
}

export const useValidSoftware = (
  hostMachineType: number,
  home: Home,
  availableSoftware: HomeSoftwareType[]
) => {
  function memoizedSoftware() {
    if (
      hostMachineType >= 0 &&
      availableSoftware &&
      availableSoftware.length >= 0 &&
      home
    ) {
      return requiresSoftwareFilter.has(hostMachineType)
        ? availableSoftware.filter((software) => softwareFilter(home, software))
        : availableSoftware
    }
    return undefined
  }
  return useMemo(memoizedSoftware, [hostMachineType, home, availableSoftware])
}

export function useSoftwareUpdates(
  home: Home | null,
  availableSoftware: HomeSoftwareType[] | undefined
) {
  if (
    availableSoftware &&
    availableSoftware.length >= 0 &&
    home?.softwareInfo
  ) {
    const softwareAheadOfCurrentVersion = (software: HomeSoftwareType) =>
      software.version.number > home?.softwareInfo.version.number &&
      software.visible

    return availableSoftware.filter(softwareAheadOfCurrentVersion)
  }
  return []
}

export function usePreviousSoftwareVersions(
  home: Home | null,
  availableSoftware: HomeSoftwareType[] | undefined
) {
  if (availableSoftware && availableSoftware.length >= 0 && home) {
    const softwareAheadOfCurrentVersion = (software: HomeSoftwareType) =>
      software.version.number < home.softwareInfo.version.number

    return availableSoftware.filter(softwareAheadOfCurrentVersion)
  }
  return []
}

const SECOND = 1000
const MINUTE = SECOND * 60
const THIRTY_MINUTES = MINUTE * 30

export function checkSoftwareUpdateInProgress(
  softwareStatus: HomeCloudSoftwareHistoryType
) {
  if (!softwareStatus) {
    return undefined
  }
  const softwareUpdatedInMilliseconds = softwareStatus.updated * 1000
  const softwareUpdateNotTimedOut =
    Date.now() - softwareUpdatedInMilliseconds < THIRTY_MINUTES
  const isPreparingDownload =
    (softwareStatus.requestedBy === SoftwareHistoryRequestedByEnum.home &&
      softwareStatus.state === CloudSoftwareHistoryEnum.download) ||
    softwareStatus.state === CloudSoftwareHistoryEnum.ready

  return (
    softwareStatus.state !== CloudSoftwareHistoryEnum.success &&
    softwareStatus.state !== CloudSoftwareHistoryEnum.error &&
    softwareUpdateNotTimedOut &&
    softwareStatus.updateId &&
    !isPreparingDownload
  )
}

export function usePermissionLogic(
  not: boolean,
  permission: boolean | null | undefined,
  truthy = false
) {
  const is = not === false && (permission === true || truthy === true)
  const isNot =
    not === true &&
    (permission === false || (truthy === true && !!permission === false))

  return is || isNot || null
}

const MAJOR = 8
const MINOR = 6
export const homeRuntimeMeetsMinimumRuntimeRequirements = (home: Home | null) =>
  home
    ? home.softwareInfo.version.major > MAJOR ||
      (home.softwareInfo.version.major >= MAJOR &&
        home.softwareInfo.version.minor >= MINOR)
    : null

export function useHostIsOffline() {
  const { isOnline } = useContext(HomeStateContext)
  return usePermissionLogic(true, isOnline)
}

export function useHomeNotConfigAccessEnabled() {
  const configAccessEnabled = useAppSelector(
    selectedHomeConfigAccessEnabledSelector
  )
  return usePermissionLogic(true, configAccessEnabled)
}
